/**
 * Class for initializing and managing courses blocks on the frontend.
 */
export default class InitCourses {
	// Static property to track all instances.
	static instances = [];

	/**
	 * Constructor
	 *
	 * @param {HTMLElement}  container The block container element.
	 * @param {Object|Array} settings  The settings object or legacy selectedCategories array.
	 */
	constructor( container, settings = {} ) {
		// Prevent duplicate initialization.
		if ( container?.dataset?.initialized === 'true' ) {
			return;
		}

		this.container = container ?? null;
		this.coursesContainer = container?.querySelector( '.atls-courses-list' ) ?? null;

		// Handle both new settings object and legacy array format.
		if ( Array.isArray( settings ) ) {
			// Legacy format: just selectedCategories array.
			this.selectedCategories = settings.length > 0 ? settings : JSON.parse( container.dataset.selectedCategories || '[]' );
			this.categoriesMatchAll = container.dataset.categoriesMatchAll === 'true';
			this.showCourseButton = container.dataset.showCourseButton === 'true';
			this.showSignUpButton = container.dataset.showSignupButton === 'true';
			this.showProtectedLabel = container.dataset.showProtectedLabel === 'true';
			this.labelShowCourse = container.dataset.labelShowCourse || 'Visa kurs';
			this.labelSignUp = container.dataset.labelSignUp || 'Anmäl mig';
			this.labelProtected = container.dataset.labelProtected ?? 'Lokal kurs (låst)';
		} else {
			// New format: settings object.
			this.selectedCategories = settings.selectedCategories ?? JSON.parse( container.dataset.selectedCategories || '[]' );
			this.categoriesMatchAll = settings.categoriesMatchAll ?? ( container.dataset.categoriesMatchAll === 'true' );
			this.showCourseButton = settings.showCourseButton ?? ( container.dataset.showCourseButton === 'true' );
			this.showSignUpButton = settings.showSignUpButton ?? ( container.dataset.showSignupButton === 'true' );
			this.showProtectedLabel = settings.showProtectedLabel ?? ( container.dataset.showProtectedLabel === 'true' );
			this.labelShowCourse = settings.labelShowCourse ?? container.dataset.labelShowCourse ?? 'Visa kurs';
			this.labelSignUp = settings.labelSignUp ?? container.dataset.labelSignUp ?? 'Anmäl mig';
			this.labelProtected = settings.labelProtected ?? container.dataset.labelProtected ?? 'Lokal kurs (låst)';
		}

		this.courses = [];
		this.blockId = container?.id || `atls-courses-${ InitCourses.instances.length }`;

		// Mark as initialized.
		container.dataset.initialized = 'true';

		// Add to instances array.
		InitCourses.instances.push( this );

		this.init();
	}

	/**
	 * Initialize the courses block.
	 *
	 * @return {void}
	 */
	init() {
		this.loadCourses();
	}

	/**
	 * Load courses from REST API.
	 *
	 * @return {void}
	 */
	loadCourses() {
		if ( ! this.coursesContainer ) {
			console.warn( `[InitCourses] No courses container found for block: ${ this.blockId }` ); // eslint-disable-line no-console
			return;
		}

		// Build query parameters.
		const params = new URLSearchParams();
		if ( this.selectedCategories && this.selectedCategories.length > 0 ) {
			this.selectedCategories.forEach( categoryId => {
				params.append( 'category_ids[]', categoryId );
			} );
			// Add the match type parameter.
			params.append( 'match_all', this.categoriesMatchAll ? 'true' : 'false' );
		}

		const queryString = params.toString();
		const apiUrl = `/wp-json/atls-courses/v1/courses${ queryString ? `?${ queryString }` : '' }`;

		// Fetch courses from REST API.
		fetch( apiUrl )
			.then( response => {
				if ( ! response.ok ) {
					throw new Error( `HTTP error! status: ${ response.status }` );
				}
				return response.json();
			} )
			.then( courses => {
				this.courses = courses;
				this.clearCourses();
				this.renderCourses();
			} )
			.catch( error => {
				console.error( `[InitCourses] Error loading courses for block ${ this.blockId }:`, error ); // eslint-disable-line no-console
				this.clearCourses();
				this.renderError();
			} );
	}

	/**
	 * Render an empty state by removing all existing rows.
	 */
	clearCourses() {
		if ( ! this.coursesContainer ) {
			console.warn( `[InitCourses] No courses container found for block: ${ this.blockId }` ); // eslint-disable-line no-console
			return;
		}
		const rows = this.coursesContainer?.querySelectorAll( '.atls-course-item, .atls-courses-list__empty, .atls-courses-list__error' );
		rows.forEach( row => row.remove() );
	}

	/**
	 * Render all courses.
	 *
	 * @return {void}
	 */
	renderCourses() {
		if ( ! this.coursesContainer ) {
			console.warn( `[InitCourses] No courses container found for block: ${ this.blockId }` ); // eslint-disable-line no-console
			return;
		}

		if ( this.courses.length === 0 ) {
			this.renderEmpty();
			return;
		}

		try {
			const rows = this.courses.map( course => this.renderCourseRow( course ) ).join( '' );
			const header = this.coursesContainer.querySelector( '.atls-courses-list__header' );
			if ( header ) {
				header.insertAdjacentHTML( 'afterend', rows );
			} else {
				this.coursesContainer.innerHTML = rows;
			}
		} catch ( error ) {
			console.error( `[InitCourses] Error rendering courses:`, error ); // eslint-disable-line no-console
			console.error( `[InitCourses] Courses data:`, this.courses ); // eslint-disable-line no-console
			this.renderError();
		}
	}

	/**
	 * Render a single course row.
	 *
	 * @param {Object} course Course data.
	 * @return {string} HTML string for course item.
	 */
	renderCourseRow( course ) {
		const weekNumber = course?.week ?? '-';
		const statusLabel = course?.status_label;
		const seats = course?.stock_quantity ?? 0;
		const location = course.location ?? '';
		let dateRange = course?.formatted_date;

		if ( course.purchasable === 'protected' && this.showProtectedLabel ) {
			dateRange += `<span class="atls-course-item__date--protected">${ this.labelProtected }</span>`;
		}

		let markup = `<div class="atls-course-item" data-id="${ course.id }">`;
		markup += `
			<div class="atls-course-item__week">${ weekNumber }</div>
			<div class="atls-course-item__title">${ location }</div>
			<div class="atls-course-item__date">${ dateRange }</div>
			<div class="atls-course-item__seats">${ seats }</div>
			<div class="atls-course-item__status">${ statusLabel }</div>
		`;

		// Build actions based on settings.
		let actionsHTML = '<div class="atls-course-item__actions">';

		if ( course.purchasable && course.purchasable === 'available' ) {
			// Show sign-up button if enabled.
			if ( this.showSignUpButton ) {
				actionsHTML += `
					<form method="get" action="">
						<input type="hidden" name="add-to-cart" value="${ course.id }" />
						<input type="hidden" name="quantity" value="1" />
						<button type="submit" class="btn atls-course-add-to-cart">
							${ this.labelSignUp }
						</button>
					</form>
				`;
			}

			// Show course button if enabled.
			if ( this.showCourseButton ) {
				actionsHTML += `<a href="${ course.permalink }">${ this.labelShowCourse } <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 32 32"><path fill="#1c1c1e" d="M10.598 8.172c.009.389.171.758.452 1.028l6.817 6.816-6.817 6.817a1.475 1.475 0 0 0 1.33 2.499c.287-.06.55-.205.754-.416l7.858-7.858a1.473 1.473 0 0 0 0-2.083l-7.858-7.859a1.473 1.473 0 0 0-2.536 1.056"/></svg></a>`;
			}
		} else if ( course.purchasable && course.purchasable === 'protected' ) {
			// Protected courses always show the course link.
			if ( this.showCourseButton ) {
				actionsHTML += `<a href="${ course.permalink }">${ this.labelShowCourse } <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 28 28"><path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M22.167 12.833H5.833A2.333 2.333 0 0 0 3.5 15.167v8.166a2.333 2.333 0 0 0 2.333 2.334h16.334a2.333 2.333 0 0 0 2.333-2.334v-8.166a2.333 2.333 0 0 0-2.333-2.334"/><path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.167 12.833V8.167a5.833 5.833 0 0 1 11.666 0v4.666"/></svg></a>`;
			}
		} else if ( course.permalink ) {
			// Default: show course link if enabled.
			if ( this.showCourseButton ) {
				actionsHTML += `<a href="${ course.permalink }">${ this.labelShowCourse } <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 32 32"><path fill="#1c1c1e" d="M10.598 8.172c.009.389.171.758.452 1.028l6.817 6.816-6.817 6.817a1.475 1.475 0 0 0 1.33 2.499c.287-.06.55-.205.754-.416l7.858-7.858a1.473 1.473 0 0 0 0-2.083l-7.858-7.859a1.473 1.473 0 0 0-2.536 1.056"/></svg></a>`;
			}
		}

		actionsHTML += '</div>';
		markup += actionsHTML;
		markup += `</div>`;

		return markup;
	}

	/**
	 * Render empty state.
	 *
	 * @return {void}
	 */
	renderEmpty() {
		if ( ! this.coursesContainer ) {
			console.warn( `[InitCourses] No courses container found for block: ${ this.blockId }` ); // eslint-disable-line no-console
			return;
		}

		const emptyHTML = `
			<div class="atls-courses-list__empty" style="grid-column: 1 / -1; text-align: center; padding: 2rem;">
				Inga kurser hittades.
			</div>
		`;

		const header = this.coursesContainer.querySelector( '.atls-courses-list__header' );
		if ( header ) {
			header.insertAdjacentHTML( 'afterend', emptyHTML );
		}
	}

	/**
	 * Render error state.
	 *
	 * @return {void}
	 */
	renderError() {
		if ( ! this.coursesContainer ) {
			console.warn( `[InitCourses] No courses container found for block: ${ this.blockId }` ); // eslint-disable-line no-console
			return;
		}

		const errorHTML = `
			<div class="atls-courses-list__error" style="grid-column: 1 / -1; text-align: center; padding: 2rem; color: #d63638;">
				Ett fel uppstod vid laddning av kurser.
			</div>
		`;
		const header = this.coursesContainer.querySelector( '.atls-courses-list__header' );
		if ( header ) {
			header.insertAdjacentHTML( 'afterend', errorHTML );
		}
	}

	/**
	 * Refresh courses data for this instance.
	 *
	 * @return {void}
	 */
	refresh() {
		this.loadCourses();
	}

	/**
	 * Destroy this instance and clean up.
	 *
	 * @return {void}
	 */
	destroy() {
		// Remove from instances array.
		const index = InitCourses.instances.indexOf( this );
		if ( index > -1 ) {
			InitCourses.instances.splice( index, 1 );
		}

		// Remove initialized flag.
		if ( this.container ) {
			delete this.container.dataset.initialized;
		}

		// Clear references.
		this.container = null;
		this.coursesContainer = null;
		this.courses = [];
	}

	/**
	 * Static method to initialize all course blocks on the page.
	 *
	 * @return {void}
	 */
	static initAll() {
		const blocks = document.querySelectorAll( '.atls-courses-block-inner-wrapper' );

		if ( blocks.length === 0 ) {
			console.warn( '[InitCourses] No course blocks found on page' ); // eslint-disable-line no-console
			return;
		}

		console.log( `[InitCourses] Initializing ${ blocks.length } course block(s)` ); // eslint-disable-line no-console

		blocks.forEach( block => {
			new InitCourses( block, [] ); // Pass empty array so constructor uses data attributes.
		} );
	}

	/**
	 * Static method to refresh all instances.
	 *
	 * @return {void}
	 */
	static refreshAll() {
		InitCourses.instances.forEach( instance => {
			instance.refresh();
		} );
	}

	/**
	 * Static method to get instance by block ID.
	 *
	 * @param {string} blockId The block ID.
	 * @return {InitCourses|null} The instance or null if not found.
	 */
	static getInstance( blockId ) {
		return InitCourses.instances.find( instance => instance.blockId === blockId ) || null;
	}

	/**
	 * Static method to destroy all instances.
	 *
	 * @return {void}
	 */
	static destroyAll() {
		// Create a copy of the array since destroy() modifies it.
		const instancesCopy = [ ...InitCourses.instances ];
		instancesCopy.forEach( instance => {
			instance.destroy();
		} );
	}
}
